import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  EditorShell,
  EditorTitle,
  EmptyState,
  FormattingToolbar,
  InspectorTabs,
  MarkdownEditor,
  type MarkdownEditorRef,
  Savebar,
  type SavebarMode,
  Skeleton,
  Toast,
  type WysiwygSaveState
} from '../components/primitives';
import { UNTITLED_WORK_ITEM_TITLE } from '../lib/editor/defaultTitles';
import { supabase } from '../lib/supabase';
import { useActiveOrg } from '../lib/activeOrg';
import type { ActiveFormats } from '../lib/markdownFormatting';
import { PropertiesPanel } from '../components/workItemDetail/PropertiesPanel';
import { WorkItemCanvasPropertyBar } from '../components/workItemDetail/WorkItemCanvasPropertyBar';
import { SubtasksChecklist } from '../components/workItemDetail/SubtasksChecklist';
import { ChildTasksPanel } from '../components/workItemDetail/ChildTasksPanel';
import { AttachedDocumentsPanel } from '../components/workItemDetail/AttachedDocumentsPanel';
import { HistoryDrawer } from '../components/HistoryDrawer';
import {
  CommentsPanel,
  type CommentRow,
  type CommentsMember
} from '../components/CommentsPanel';
import type { Database } from '../types/database';
import { isIDE } from '../lib/isIDE';
import { useVersionHistoryVM } from '../lib/vm/useVersionHistoryVM';
import { useRealtimeRowReload } from '../hooks/useRealtimeRowReload';
import { useTrackOpenItem } from '../hooks/useTrackOpenItem';
import { useAgentWriteSubscription } from '../hooks/useAgentWriteSubscription';
import { AgentWriteToast } from '../components/agent/AgentWriteToast';
import { AgentArchiveBanner } from '../components/agent/AgentArchiveBanner';
import type { McpWriteCommittedFrame } from '../lib/agentProtocol';
import { snapshotEditorViewport } from '../lib/editor/preserveEditorViewport';
import './WorkItemDetail.css';

type WorkItemType = Database['public']['Enums']['work_item_type'];

interface LoadedWorkItem {
  id: string;
  org_id: string;
  type: WorkItemType;
  name: string;
  body: string;
  status: string;
  parent_work_item_id: string | null;
  project_id: string | null;
  build_order: number | null;
  due_date: string | null;
  start_date: string | null;
  end_date: string | null;
  updated_at: string;
}

interface ParentChip {
  id: string;
  name: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'notfound' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; row: LoadedWorkItem };

interface RawComment {
  id: string;
  parentCommentId: string | null;
  authorId: string;
  body: string;
  createdAt: string;
  editedAt: string | null;
  resolvedAt: string | null;
}

export interface WorkItemDetailViewProps {
  /** Work item id to load. */
  workItemId: string | undefined;
  /** `'route'` = standalone page; `'cover'` = embedded in a FullScreenCover. */
  surface?: 'route' | 'cover';
  /** Cover mode: open a related work item (parent/child/subtask) in place. */
  onOpenItem?: (workItemId: string) => void;
  /** Cover mode: report unsaved-changes state so the host can guard close. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * The work-item editor body. Rendered as a standalone page by `WorkItemDetail`
 * (route) and embedded inside a `FullScreenCover` by `EditorCover` (cover).
 */
export function WorkItemDetailView({
  workItemId,
  surface = 'route',
  onOpenItem,
  onDirtyChange
}: WorkItemDetailViewProps) {
  const id = workItemId;
  const navigate = useNavigate();
  // Active org owned by the shell. Every work_items fetch is scoped to it so a
  // deep-link to an item in another org (which RLS would allow for a member of
  // both) resolves to "not found" instead of rendering cross-org.
  const { activeOrgId } = useActiveOrg();

  // In a cover, related-item links swap the cover's item in place; as a route
  // they navigate laterally.
  const openWorkItem = useCallback(
    (targetId: string) => {
      if (surface === 'cover') {
        onOpenItem?.(targetId);
        return;
      }
      navigate(`/work-item-detail/${targetId}`, {
        state: { routeTransition: 'lateral' }
      });
    },
    [surface, onOpenItem, navigate]
  );

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  // Record the open work item in the cross-route store so the standalone Agent
  // panel reports it as active context. This is the OPENED item (route id), not
  // an edit-driven value — see openItemStore / useTrackOpenItem. The title is
  // the LOADED name (not the editable draft), resolving a moment after the id.
  const openItemTitle = load.kind === 'ready' ? load.row.name : undefined;
  useTrackOpenItem('workItem', id, openItemTitle);

  const historyVM = useVersionHistoryVM('work_item', id);
  const [parent, setParent] = useState<ParentChip | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [parentWorkItemId, setParentWorkItemId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [buildOrder, setBuildOrder] = useState<number | null>(null);
  const [saveState, setSaveState] = useState<WysiwygSaveState>('idle');
  const [toast, setToast] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rawComments, setRawComments] = useState<RawComment[]>([]);
  const [commentsTick, setCommentsTick] = useState(0);
  const [members, setMembers] = useState<CommentsMember[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const expectedTsRef = useRef<string | null>(null);
  const baselineTitleRef = useRef('');
  const baselineBodyRef = useRef('');
  // In-flight-save guard. Set while a user-initiated `onSave` is mid-RPC so a
  // concurrent `reloadFromServer` (agent-write refresh, Task 3.3) does not stomp
  // the baseline/timestamp refs the save is actively mutating. A ref — not state
  // — so it is readable synchronously inside both callbacks.
  const saveInFlightRef = useRef(false);
  // Own-write guard. Stamped on every local property/title/body commit so the
  // realtime UPDATE the write triggers on our own row is NOT misattributed to an
  // agent (the `set_work_item_project` / `save_work_item` RPCs bump the row, and
  // its realtime echo arrives back at this same tab). A real external write — a
  // second tab, the agent CLI — never stamps this tab's ref, so it still toasts.
  // `performance.now()` (monotonic ms) — a ref, not state, for synchronous reads
  // inside the realtime/agent callbacks. Seeded to -Infinity (NOT 0): right after
  // page load `performance.now()` is itself small, so a 0 seed would make the
  // `now - ref < 3000` window spuriously true for an external write arriving in
  // the first 3s — wrongly suppressing its toast. -Infinity keeps the guard false
  // until an actual local write stamps it.
  const recentLocalWriteRef = useRef(-Infinity);
  const markLocalWrite = useCallback(() => {
    recentLocalWriteRef.current = performance.now();
  }, []);
  const isOwnWrite = useCallback(
    () => performance.now() - recentLocalWriteRef.current < 3000,
    []
  );

  // V2 relayout state — editor mode (Edit | Preview), right-rail inspector
  // open state (defaults to open so the panel is visible on first paint), and
  // active inspector tab. The MarkdownEditor ref lets the floating
  // FormattingToolbar splice markdown without holding the textarea ref.
  const [editorMode, setEditorMode] = useState<SavebarMode>('edit');
  const [railOpen, setRailOpen] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<
    'properties' | 'subtasks' | 'attached-docs' | 'comments'
  >('properties');
  const bodyRef = useRef<MarkdownEditorRef | null>(null);
  const [activeFormats, setActiveFormats] = useState<ActiveFormats>({});
  // The editor stage is the scrolling column (`.pn-editor-shell-stage`); the
  // reconcile snapshots + restores scroll/caret around the inline content swap
  // so an agent write never jumps the work-item view.
  const shellRef = useRef<HTMLElement | null>(null);

  const ideBuild = isIDE();

  const openInspector = () => {
    setHistoryOpen(false);
    setRailOpen(true);
  };
  const closeInspector = () => setRailOpen(false);
  const openHistory = () => {
    setRailOpen(false);
    setHistoryOpen(true);
  };
  const closeHistory = () => setHistoryOpen(false);
  const toggleInspector = () =>
    railOpen ? closeInspector() : openInspector();
  const toggleHistory = () => (historyOpen ? closeHistory() : openHistory());

  // Apply a freshly-fetched work-item row to local state: reset the
  // baseline/timestamp refs used for change detection AND seed the editable
  // drafts + property values from the row. Shared by the initial load and
  // `reloadFromServer` so both stay in lockstep.
  const applyRow = useCallback((row: LoadedWorkItem) => {
    expectedTsRef.current = row.updated_at;
    baselineTitleRef.current = row.name ?? '';
    baselineBodyRef.current = row.body ?? '';
    setTitleDraft(row.name ?? '');
    setBodyDraft(row.body ?? '');
    setStatusValue(row.status ?? '');
    setDueDate(row.due_date ?? null);
    setStartDate(row.start_date ?? null);
    setEndDate(row.end_date ?? null);
    setParentWorkItemId(row.parent_work_item_id ?? null);
    setProjectId(row.project_id ?? null);
    setBuildOrder(row.build_order ?? null);
  }, []);

  // Optimistic property setters handed to the property panels, each wrapped to
  // stamp `recentLocalWrite` first. The panels own the actual RPC (status /
  // project / parent / dates) and call these back optimistically; stamping here
  // means the realtime echo of that RPC is recognised as our own write and does
  // NOT raise the "Agent updated this work item" toast. Covers status / parent /
  // dates too, not just project — the toast misattribution was latent for all of
  // them. The values still reconcile silently via `reloadFromServer`.
  const handleStatusChange = useCallback(
    (next: string) => {
      markLocalWrite();
      setStatusValue(next);
    },
    [markLocalWrite]
  );
  const handleDueDateChange = useCallback(
    (next: string | null) => {
      markLocalWrite();
      setDueDate(next);
    },
    [markLocalWrite]
  );
  const handleStartDateChange = useCallback(
    (next: string | null) => {
      markLocalWrite();
      setStartDate(next);
    },
    [markLocalWrite]
  );
  const handleEndDateChange = useCallback(
    (next: string | null) => {
      markLocalWrite();
      setEndDate(next);
    },
    [markLocalWrite]
  );
  const handleParentChange = useCallback(
    (next: string | null) => {
      markLocalWrite();
      setParentWorkItemId(next);
    },
    [markLocalWrite]
  );
  const handleProjectChange = useCallback(
    (next: string | null) => {
      markLocalWrite();
      setProjectId(next);
    },
    [markLocalWrite]
  );
  const handleBuildOrderChange = useCallback(
    (next: number | null) => {
      markLocalWrite();
      setBuildOrder(next);
    },
    [markLocalWrite]
  );

  useEffect(() => {
    if (!id) return;
    // Active org not resolved yet — stay loading; the effect re-runs once the
    // shell resolves it (and on org switch, so an out-of-org item goes notfound).
    if (!activeOrgId) return;
    let cancelled = false;
    setLoad({ kind: 'loading' });
    setParent(null);
    setSaveState('idle');
    void (async () => {
      const { data, error } = await supabase
        .from('work_items')
        .select(
          'id, org_id, type, name, body, status, parent_work_item_id, project_id, build_order, due_date, start_date, end_date, updated_at'
        )
        .eq('id', id)
        .eq('org_id', activeOrgId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setLoad({ kind: 'error', message: error.message });
        return;
      }
      if (!data) {
        setLoad({ kind: 'notfound' });
        return;
      }
      const row = data as LoadedWorkItem;
      applyRow(row);
      setLoad({ kind: 'ready', row });
    })();
    return () => {
      cancelled = true;
    };
  }, [id, applyRow, activeOrgId]);

  /**
   * Silent background refresh of server-confirmed work-item state (Phase 3,
   * Task 3.3). Re-runs the same `SELECT` the initial load runs, then resets the
   * baseline/timestamp refs, the editable drafts, and the in-flight `load`
   * ready row from the fresh row. Used when a committed agent write to the OPEN
   * work item arrives.
   *
   * Invariants (mirroring the Document hook's contract):
   *  - `load.kind` MUST stay `'ready'` throughout — this is a background
   *    refresh, not a visible reload, so it never flips to `'loading'`.
   *  - It defers entirely when a user-initiated save is in flight
   *    (`saveInFlightRef`), so it cannot stomp the refs the save is mutating.
   *  - It is a no-op unless the work item is already loaded with a known id, so
   *    it cannot resurrect a `notfound`/`error` view.
   */
  const reloadFromServer = useCallback(async () => {
    if (!id) return;
    if (saveInFlightRef.current) return;
    if (load.kind !== 'ready') return;
    if (!activeOrgId) return;

    const { data, error } = await supabase
      .from('work_items')
      .select(
        'id, org_id, type, name, body, status, parent_work_item_id, project_id, due_date, start_date, end_date, updated_at'
      )
      .eq('id', id)
      .eq('org_id', activeOrgId)
      .maybeSingle();

    // A save may have started between the guard check above and the SELECT
    // resolving; if so, abandon this refresh so the save stays authoritative.
    if (saveInFlightRef.current) return;
    if (error || !data) return;

    const row = data as LoadedWorkItem;
    // Snapshot the editor viewport (scroll + caret) BEFORE the draft swap so the
    // controlled-textarea value replacement in `applyRow` — which collapses the
    // caret and can shift the scrolling stage — is invisible to the user.
    // Restored on the next frame, after React commits the new value.
    const snapshot = snapshotEditorViewport(
      shellRef.current?.querySelector<HTMLElement>('.pn-editor-shell-stage')
    );
    applyRow(row);
    // Mutate the in-flight ready row in place. `load.kind` never leaves
    // `'ready'`, so the page does not flash a spinner or remount children.
    setLoad((prev) => (prev.kind === 'ready' ? { kind: 'ready', row } : prev));
    snapshot.restore();
  }, [id, load.kind, applyRow, activeOrgId]);

  // Track the parent chip whenever parent_work_item_id changes (picker edit
  // on Story/Bug/Task, or the initial load). NULL ⇒ clear the chip.
  useEffect(() => {
    let cancelled = false;
    if (!parentWorkItemId) {
      setParent(null);
      return;
    }
    if (!activeOrgId) return;
    void (async () => {
      const { data } = await supabase
        .from('work_items')
        .select('id, name')
        .eq('id', parentWorkItemId)
        .eq('org_id', activeOrgId)
        .maybeSingle();
      if (cancelled || !data) return;
      setParent({
        id: data.id as string,
        name: (data.name as string) ?? ''
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [parentWorkItemId, activeOrgId]);

  useEffect(() => {
    if (load.kind !== 'ready') return;
    let cancelled = false;
    void (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (cancelled) return;
      if (sess.session) setCurrentUserId(sess.session.user.id);
      const { data: memberRows } = await supabase.rpc('list_org_members', {
        p_org_id: load.row.org_id
      });
      if (cancelled || !Array.isArray(memberRows)) return;
      const mapped: CommentsMember[] = (
        memberRows as Array<{
          user_id: string | null;
          display_name: string | null;
          username: string | null;
          kind: string | null;
        }>
      )
        .filter((m) => m.kind === 'active' && !!m.user_id)
        .map((m) => ({
          id: m.user_id!,
          name: m.display_name ?? m.username ?? 'Member',
          handle: m.username ?? undefined
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setMembers(mapped);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('comments')
        .select(
          'id, parent_comment_id, author_id, body, created_at, edited_at, resolved_at'
        )
        .eq('subject_kind', 'work_item')
        .eq('subject_id', id)
        .order('created_at', { ascending: true });
      if (cancelled || !data) return;
      setRawComments(
        (data as Array<{
          id: string;
          parent_comment_id: string | null;
          author_id: string;
          body: string;
          created_at: string;
          edited_at: string | null;
          resolved_at: string | null;
        }>).map((c) => ({
          id: c.id,
          parentCommentId: c.parent_comment_id,
          authorId: c.author_id,
          body: c.body,
          createdAt: c.created_at,
          editedAt: c.edited_at,
          resolvedAt: c.resolved_at
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [id, commentsTick]);

  // When a restore mints a new snapshot, refresh the editor drafts from the
  // server so they show the restored content.
  // The VM loads snapshots on mount, so restoreSyncTick transitions
  // null -> <id> on first load; that initial value must NOT trigger a
  // refetch (it would clobber unsaved drafts in a load-vs-edit race).
  // restoreSyncSeenRef records the first observed tick and we only act on
  // a change that happens after it.
  const restoreSyncTick =
    historyVM.snapshots.find((s) => s.sourceVersionId != null)?.id ?? null;
  const restoreSyncSeenRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (restoreSyncSeenRef.current === undefined) {
      // First run — record the baseline tick, do not act on initial load.
      restoreSyncSeenRef.current = restoreSyncTick;
      return;
    }
    if (restoreSyncTick === restoreSyncSeenRef.current) return;
    restoreSyncSeenRef.current = restoreSyncTick;
    if (!id || load.kind !== 'ready') return;
    if (!activeOrgId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('work_items')
        .select('name, body, updated_at')
        .eq('id', id)
        .eq('org_id', activeOrgId)
        .maybeSingle();
      if (cancelled) return;
      if (!data) return;
      expectedTsRef.current = data.updated_at;
      baselineTitleRef.current = data.name;
      baselineBodyRef.current = data.body;
      setTitleDraft(data.name);
      setBodyDraft(data.body);
      // A completed restore syncs the editor to server state, which also
      // clears any stale conflict banner.
      setSaveState('saved');
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreSyncTick]);

  const nameByUserId = useMemo(() => {
    const m = new Map<string, string>();
    members.forEach((mem) => m.set(mem.id, mem.name));
    return m;
  }, [members]);

  const commentRows: CommentRow[] = useMemo(
    () =>
      rawComments.map((c) => ({
        ...c,
        authorName: nameByUserId.get(c.authorId) ?? 'Unknown'
      })),
    [rawComments, nameByUserId]
  );

  const onAddComment = useCallback(
    async (input: { body: string; parentCommentId: string | null }) => {
      if (!id || !currentUserId) return;
      const { error } = await supabase.from('comments').insert({
        subject_kind: 'work_item',
        subject_id: id,
        parent_comment_id: input.parentCommentId,
        body: input.body,
        author_id: currentUserId
      });
      if (error) setToast(error.message ?? 'Comment failed.');
      setCommentsTick((n) => n + 1);
    },
    [id, currentUserId]
  );

  const onEditComment = useCallback(
    async (input: { id: string; body: string }) => {
      const { error } = await supabase
        .from('comments')
        .update({ body: input.body, edited_at: new Date().toISOString() })
        .eq('id', input.id);
      if (error) setToast(error.message ?? 'Edit failed.');
      setCommentsTick((n) => n + 1);
    },
    []
  );

  const onDeleteComment = useCallback(async (commentId: string) => {
    const { error } = await supabase
      .from('comments')
      .delete()
      .eq('id', commentId);
    if (error) setToast(error.message ?? 'Delete failed.');
    setCommentsTick((n) => n + 1);
  }, []);

  const onResolveComment = useCallback(
    async (commentId: string) => {
      if (!currentUserId) return;
      const { error } = await supabase
        .from('comments')
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: currentUserId
        })
        .eq('id', commentId);
      if (error) setToast(error.message ?? 'Resolve failed.');
      setCommentsTick((n) => n + 1);
    },
    [currentUserId]
  );

  const onReopenComment = useCallback(async (commentId: string) => {
    const { error } = await supabase
      .from('comments')
      .update({ resolved_at: null, resolved_by: null })
      .eq('id', commentId);
    if (error) setToast(error.message ?? 'Reopen failed.');
    setCommentsTick((n) => n + 1);
  }, []);

  const titleDirty =
    load.kind === 'ready' && titleDraft !== baselineTitleRef.current;
  const bodyDirty =
    load.kind === 'ready' && bodyDraft !== baselineBodyRef.current;
  const dirty = titleDirty || bodyDirty;

  // Cover host guards close on unsaved changes; report dirty up.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Phase 3, Task 3.3: agent-write reactions for the OPEN work item.
  // `agentToastShown` drives the bottom-right `<AgentWriteToast>` after a
  // committed update to this item; `agentArchived` flips true after a committed
  // delete, swapping the title bar for a non-dismissible archive banner.
  const [agentToastShown, setAgentToastShown] = useState(false);
  const [agentArchived, setAgentArchived] = useState(false);

  // The committed-write handler for THIS work item (the hook has already
  // filtered to our kind+id, so every frame here targets the open item).
  //
  //  - update_work_item + targetMatchesActive → show the update toast; reload
  //    from the server ONLY when the editor is NOT dirty. Reloading while dirty
  //    would silently clobber the user's unsaved title/body edits — the
  //    data-loss guard (WorkItemDetail keeps editable title + body drafts).
  //  - delete_work_item → raise the archive banner; the router must NOT
  //    auto-navigate.
  //  - create_work_item (and any unrelated tool) is a no-op at the page level —
  //    the provider owns the create toast (Task 3.4).
  const onAgentWrite = useCallback(
    (frame: McpWriteCommittedFrame) => {
      switch (frame.tool) {
        // update_work_item / set_work_item_property reconcile identically: a
        // property edit (status / assignee / priority / custom) committed by the
        // agent surfaces the toast and reloads the open detail view so the change
        // reflects inline, exactly like a body update.
        case 'update_work_item':
        case 'set_work_item_property': {
          if (!frame.targetMatchesActive) return;
          // Suppress the toast for this tab's own recent commit (the RPC's
          // realtime echo); still reconcile silently below.
          if (!isOwnWrite()) setAgentToastShown(true);
          if (!dirty) void reloadFromServer();
          return;
        }
        case 'delete_work_item': {
          setAgentArchived(true);
          return;
        }
        default:
          return;
      }
    },
    [dirty, reloadFromServer, isOwnWrite]
  );

  // Source-agnostic live reconcile. Subscribe to Supabase Realtime changes on
  // the OPEN work item's row and reconcile inline — works for ANY external
  // writer (real agent CLI, a second tab, a server-side RPC), not just writes
  // that flow through the IDE's WebSocket frame bus. NOT gated on isIDE().
  //
  // `dirty` is read through a ref so the subscription does not churn on every
  // keystroke; the realtime callbacks mirror the agent-frame handler exactly:
  //  - external UPDATE → surface the update toast; reload from the server ONLY
  //    when the editor is NOT dirty (reloadFromServer also defers an in-flight
  //    save and resets drafts wholesale, so guarding on !dirty is the
  //    unsaved-edit/caret data-loss guard).
  //  - external DELETE → raise the non-dismissible archive banner.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const onRealtimeChange = useCallback(() => {
    // Skip the agent toast when this UPDATE is the echo of our own recent RPC
    // write (project/status/parent/date/save); a real external write never
    // stamped this tab's ref, so it still toasts. Reconcile silently regardless.
    if (!isOwnWrite()) setAgentToastShown(true);
    if (!dirtyRef.current) void reloadFromServer();
  }, [reloadFromServer, isOwnWrite]);
  const onRealtimeDelete = useCallback(() => {
    setAgentArchived(true);
  }, []);
  useRealtimeRowReload({
    table: 'work_items',
    rowId: load.kind === 'ready' ? load.row.id : null,
    onChange: onRealtimeChange,
    onDelete: onRealtimeDelete
  });

  const onSave = useCallback(async () => {
    if (load.kind !== 'ready' || !id) return;
    if (!dirty) return;
    setSaveState('saving');
    const expected = expectedTsRef.current;
    if (!expected) {
      setSaveState('failed');
      setToast('Save failed: no baseline timestamp.');
      return;
    }
    // Mark the save in flight so a concurrent `reloadFromServer` (Task 3.3)
    // defers and does not stomp the baseline/timestamp refs we mutate below.
    saveInFlightRef.current = true;
    // Stamp the own-write guard: `save_work_item` bumps the row, whose realtime
    // echo returns to this tab — without this it would surface a false agent toast.
    markLocalWrite();
    try {
      const { data, error } = await supabase.rpc('save_work_item', {
        p_work_item_id: id,
        p_name: titleDraft,
        p_body: bodyDraft,
        p_expected_updated_at: expected
      });
      if (error) {
        // P0001 conflict OR any other failure — surface and stop.
        if (error.code === 'P0001') {
          setSaveState('conflict');
          return;
        }
        setSaveState('failed');
        setToast(error.message ?? 'Save failed.');
        return;
      }
      const ret = (data ?? {}) as { ok?: boolean; updated_at?: string };
      if (!ret.ok || !ret.updated_at) {
        setSaveState('failed');
        setToast('Save failed.');
        return;
      }
      expectedTsRef.current = ret.updated_at;
      baselineTitleRef.current = titleDraft;
      baselineBodyRef.current = bodyDraft;
      setSaveState('saved');
      historyVM.actions.reload();
    } finally {
      saveInFlightRef.current = false;
    }
  }, [load.kind, id, dirty, titleDraft, bodyDraft, historyVM, markLocalWrite]);

  // Page-level Cmd/Ctrl+S handler — saves title + body regardless of where
  // focus is (title field, body editor, properties panel input, comments
  // composer, etc.). The body editor stops propagation on its own Cmd+S
  // handler so we never double-fire when focus is inside the editor.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isSaveShortcut =
        (e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S');
      if (!isSaveShortcut) return;
      e.preventDefault();
      if (!dirty || saveState === 'saving' || saveState === 'conflict') return;
      void onSave();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dirty, saveState, onSave]);

  if (load.kind === 'loading') {
    return (
      <section
        className="wid-page"
        data-testid="work-item-detail"
        data-status="loading"
      >
        <Skeleton width={320} height={32} />
        <Skeleton width="100%" height={20} />
      </section>
    );
  }

  if (load.kind === 'notfound') {
    return (
      <section
        className="wid-page"
        data-testid="work-item-detail"
        data-status="notfound"
      >
        <EmptyState data-testid="work-item-detail-notfound">
          Work item not found.
        </EmptyState>
      </section>
    );
  }

  if (load.kind === 'error') {
    return (
      <section
        className="wid-page"
        data-testid="work-item-detail"
        data-status="error"
      >
        <EmptyState data-testid="work-item-detail-error">
          {load.message}
        </EmptyState>
      </section>
    );
  }

  const row = load.row;
  // In a cover the FullScreenCover header owns close, so the Savebar
  // "← Work Items" router Link is suppressed (it would change the route behind
  // the cover).
  const backTarget =
    surface === 'cover' ? undefined : { to: '/work-items', label: 'Work Items' };
  return (
    <section
      ref={shellRef}
      className="wid-page"
      data-testid="work-item-detail"
      data-status="ready"
      data-surface={surface}
      data-work-item-id={row.id}
      data-work-item-type={row.type}
    >
      {ideBuild ? (
        <WorkItemSavebarIDE
          base={{
            back: backTarget,
            saveState,
            dirty,
            onSave,
            onHistoryOpen: toggleHistory,
            historyOpen,
            mode: editorMode,
            onModeChange: setEditorMode,
            railOpen,
            onRailToggle: toggleInspector,
            testIdPrefix: 'work-item-detail'
          }}
        />
      ) : (
        <Savebar
          back={backTarget}
          saveState={saveState}
          dirty={dirty}
          onSave={onSave}
          onHistoryOpen={toggleHistory}
          historyOpen={historyOpen}
          mode={editorMode}
          onModeChange={setEditorMode}
          railOpen={railOpen}
          onRailToggle={toggleInspector}
          testIdPrefix="work-item-detail"
        />
      )}

      <EditorShell
        data-testid="work-item-detail-shell"
        data-rail-open={railOpen || historyOpen ? 'true' : 'false'}
        data-chat-open="false"
      >
        <EditorShell.Pane>
          <EditorShell.Stage>
            <EditorShell.Canvas>
              {parent ? (
                <div className="wid-header-row">
                  <button
                    type="button"
                    className="wid-parent-chip"
                    onClick={() => openWorkItem(parent.id)}
                    data-testid="work-item-detail-parent-chip"
                    data-parent-id={parent.id}
                  >
                    ↰ {parent.name || 'Parent'}
                  </button>
                </div>
              ) : null}
              <WorkItemCanvasPropertyBar
                workItemId={row.id}
                orgId={row.org_id}
                workItemType={row.type}
                workItemStatus={statusValue}
                workItemDueDate={dueDate}
                workItemStartDate={startDate}
                workItemEndDate={endDate}
                parentWorkItemId={parentWorkItemId}
                parentName={parent?.name ?? null}
                workItemProjectId={projectId}
                onStatusChange={handleStatusChange}
                onDueDateChange={handleDueDateChange}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onParentChange={handleParentChange}
                onProjectChange={handleProjectChange}
                onError={(msg) => setToast(msg)}
                onActivityChange={() => historyVM.actions.reload()}
                editable={editorMode === 'edit'}
                testIdPrefix="work-item-detail-property-bar"
              />
              <header className="wid-header">
                {agentArchived ? (
                  <AgentArchiveBanner mode="archive-work-item" />
                ) : (
                  <EditorTitle
                    id="work-item-detail-title"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    data-testid="work-item-detail-title"
                    selectAllOnFocus={titleDraft === UNTITLED_WORK_ITEM_TITLE}
                  />
                )}
              </header>
              <MarkdownEditor
                ref={bodyRef}
                value={bodyDraft}
                onChange={setBodyDraft}
                onSave={() => void onSave()}
                saveState={saveState}
                mode={editorMode}
                onActiveFormatsChange={setActiveFormats}
                dataTestId="work-item-detail-body"
              />
            </EditorShell.Canvas>
          </EditorShell.Stage>
          <FormattingToolbar
            visible={editorMode === 'edit'}
            onInsert={(action) => bodyRef.current?.insertSyntax(action)}
            activeActions={activeFormats}
            className="wid-v2-float-toolbar"
            dataTestId="work-item-detail-format-toolbar"
          />
        </EditorShell.Pane>
        <InspectorTabs
          open={railOpen}
          onClose={closeInspector}
          activeTab={inspectorTab}
          onTabChange={(v) =>
            setInspectorTab(
              v as 'properties' | 'attached-docs' | 'comments'
            )
          }
          ariaLabel="Work item inspector"
          dataTestId="work-item-detail-inspector"
          wide
          tabs={[
            {
              value: 'properties',
              label: 'Properties',
              panel: (
                <PropertiesPanel
                  workItemId={row.id}
                  orgId={row.org_id}
                  workItemType={row.type}
                  workItemStatus={statusValue}
                  workItemDueDate={dueDate}
                  workItemStartDate={startDate}
                  workItemEndDate={endDate}
                  parentWorkItemId={parentWorkItemId}
                  parentName={parent?.name ?? null}
                  workItemProjectId={projectId}
                  workItemBuildOrder={buildOrder}
                  onStatusChange={handleStatusChange}
                  onDueDateChange={handleDueDateChange}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                  onParentChange={handleParentChange}
                  onProjectChange={handleProjectChange}
                  onBuildOrderChange={handleBuildOrderChange}
                  onError={(msg) => setToast(msg)}
                  onActivityChange={() => historyVM.actions.reload()}
                  naked
                />
              )
            },
            ...(row.type === 'subtask'
              ? []
              : [
                  {
                    value: 'subtasks',
                    label: row.type === 'epic' ? 'Children' : 'Subtasks',
                    panel:
                      row.type === 'epic' ? (
                        <ChildTasksPanel
                          epicId={row.id}
                          orgId={row.org_id}
                          onNavigate={(childId) => openWorkItem(childId)}
                          naked
                        />
                      ) : (
                        <SubtasksChecklist
                          workItemId={row.id}
                          orgId={row.org_id}
                          workItemType={row.type}
                          onNavigate={(subId) => openWorkItem(subId)}
                          onError={(msg) => setToast(msg)}
                          naked
                        />
                      )
                  }
                ]),
            {
              value: 'attached-docs',
              label: 'Attached docs',
              panel: (
                <AttachedDocumentsPanel
                  workItemId={row.id}
                  orgId={row.org_id}
                  onError={(msg) => setToast(msg)}
                  naked
                />
              )
            },
            {
              value: 'comments',
              label: 'Comments',
              panel: (
                <CommentsPanel
                  subjectKind="work_item"
                  subjectId={row.id}
                  comments={commentRows}
                  currentUserId={currentUserId}
                  onAddComment={onAddComment}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                  onResolveComment={onResolveComment}
                  onReopenComment={onReopenComment}
                  naked
                />
              )
            }
          ]}
        />
        <HistoryDrawer
          subjectKind="work_item"
          subjectId={row.id}
          open={historyOpen}
          onClose={closeHistory}
          liveTitle={titleDraft}
          liveBody={bodyDraft}
          vm={historyVM}
        />

        {/*
         * IDE-only agent-write listener. Split into its own component so
         * `useAgentWriteSubscription` (which calls `useAgentSession`, requiring
         * the IDE-only AgentSessionProvider) is NEVER invoked in the plain web
         * build. Renders nothing — it only wires the subscription.
         */}
        {ideBuild ? (
          <WorkItemAgentWriteListener
            workItemId={row.id}
            onAgentWrite={onAgentWrite}
          />
        ) : null}

        {/* Agent-write toast slot — bottom-right of the editor shell. The
         * AgentWriteToast wrapper owns no positioning; the slot below does. */}
        {agentToastShown ? (
          <div className="wid-agent-toast-slot">
            <AgentWriteToast
              mode="update-work-item"
              onDismiss={() => setAgentToastShown(false)}
            />
          </div>
        ) : null}
      </EditorShell>

      {toast ? (
        <Toast onDismiss={() => setToast(null)}>{toast}</Toast>
      ) : null}
    </section>
  );
}


interface WorkItemAgentWriteListenerProps {
  workItemId: string;
  onAgentWrite: (frame: McpWriteCommittedFrame) => void;
}

/**
 * IDE-only subscriber to committed agent writes targeting the OPEN work item
 * (Phase 3, Task 3.3). Mounted only in the IDE build (the caller gates on
 * `isIDE()`), so `useAgentWriteSubscription` — which calls `useAgentSession`,
 * requiring the IDE-only `AgentSessionProvider` — is never invoked in the plain
 * web build. The hook filters frames to `workItem` + this id, so the parent's
 * handler only ever sees writes that landed on the open work item. Renders
 * nothing.
 */
function WorkItemAgentWriteListener({
  workItemId,
  onAgentWrite
}: WorkItemAgentWriteListenerProps) {
  useAgentWriteSubscription('workItem', workItemId, onAgentWrite);
  return null;
}

/**
 * IDE-only Savebar wrapper for the Work Item view. Split out so `useAgentSession`
 * is not called in the plain web build.
 */
function WorkItemSavebarIDE(props: {
  base: ComponentProps<typeof Savebar>;
}) {
  return <Savebar {...props.base} />;
}

/** Route entry: `/work-item-detail/:id`. */
export function WorkItemDetail() {
  const { id } = useParams<{ id: string }>();
  return <WorkItemDetailView workItemId={id} surface="route" />;
}

